<template>
  <v-container fluid>
    <v-row class="text-center" style="padding-top: 16px; padding-bottom: 8px">
      <v-col cols="12">
        <div class="text-h4 font-weight-black mb-1">Top and Least Performers</div>
        <div class="text-body-1 text--secondary">
          Based on time records from {{ startDateFormatted }} to {{ endDateFormatted }}
        </div>
      </v-col>
    </v-row>

    <v-divider class="mx-4 mb-4" />

    <v-row justify="end" style="padding: 0 16px">
      <v-col cols="auto">
        <v-select
          v-model="limit"
          label="N"
          :items="limitOptions"
          dense
          outlined
        />
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-btn color="primary" @click="exportToExcel" :loading="$apollo.loading">Export to Excel</v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="mb-4">
          <v-card-title class="subtitle-1">Top {{ limit }} - High to Low</v-card-title>
          <v-data-table
            :headers="topHeaders"
            :items-per-page="limit"
            dense
            item-key="name"
            :items="topN"
          >
            <template v-slot:item.score="{ item }">
              <span>{{ fmt(item.score) }}</span>
            </template>
            <template v-slot:item.absentDays="{ item }">
              <span>{{ item.absentDays - item.unApprovedAbsent }}</span>
            </template>
            <template v-slot:item.unApprovedAbsent="{ item }">
              <span>{{ item.unApprovedAbsent }}</span>
            </template>
            <template v-slot:item.lateHrs="{ item }">
              <span>{{ fmt(item.lateHrs) }}</span>
            </template>
            <template v-slot:item.missedPunches="{ item }">
              <span>{{ item.missedPunches }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="subtitle-1">Least {{ limit }} - Low to High</v-card-title>
          <v-data-table
            :headers="leastHeaders"
            :items-per-page="limit"
            dense
            item-key="name"
            :items="leastN"
          >
            <template v-slot:item.score="{ item }">
              <span>{{ fmt(item.score) }}</span>
            </template>
            <template v-slot:item.absentDays="{ item }">
              <span>{{ item.absentDays - item.unApprovedAbsent }}</span>
            </template>
            <template v-slot:item.unApprovedAbsent="{ item }">
              <span>{{ item.unApprovedAbsent }}</span>
            </template>
            <template v-slot:item.lateHrs="{ item }">
              <span>{{ fmt(item.lateHrs) }}</span>
            </template>
            <template v-slot:item.missedPunches="{ item }">
              <span>{{ item.missedPunches }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import moment from 'moment'
  import { mapGetters, mapState } from 'vuex'
  import { Q_GET_TIME_PERFORMANCE_REPORT } from '@/gql/command'
  import XLSX from 'xlsx'

  export default {
    name: 'TimePerformanceReport',
    inject: ['$refs'],
    data: () => ({
      limit: 5,
      limitOptions: [5, 10, 15, 20, 30, 40, 50, 70, 90, 100],
      topN: [],
      leastN: [],
    }),
  methods: {
    fmt (val) {
      if (val == null) return ''
      return Number(val).toFixed(2)
    },
    prependHeader (dataRows) {
      const title = 'Top and Least Performers'
      const subtitle = `Based on time records from ${this.startDateFormatted} to ${this.endDateFormatted}`
      const cols = ['Name', 'Score', this.groupLabel, 'Job Title', 'Absent Days', 'Unapproved Absent', 'Late Hrs', 'Missed Punches']
      const data = dataRows.map(r => [
        r.name || '',
        this.fmt(r.score),
        r.group || '',
        r.jobTitle || '',
        r.absentDays - r.unApprovedAbsent,
        r.unApprovedAbsent,
        this.fmt(r.lateHrs),
        r.missedPunches,
      ])
      const aoa = [
        [title],
        [subtitle],
        cols,
        ...data,
      ]
      const ws = XLSX.utils.aoa_to_sheet(aoa)
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: cols.length - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: cols.length - 1 } },
      ]
      return ws
    },
    exportToExcel () {
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, this.prependHeader(this.topN), 'Top Performers')
      XLSX.utils.book_append_sheet(wb, this.prependHeader(this.leastN), 'Least Performers')
      XLSX.writeFile(wb, `TimePerformanceReport.xlsx`)
    },
  },
    computed: {
      ...mapState('records', ['view', 'currentDate', 'startDate', 'endDate']),
      ...mapGetters(['team', 'groupLabel', 'parentGroupLabel', 'groupLabelPlural', 'parentGroupLabelPlural']),
      topHeaders () {
        return [
          { text: 'Name', value: 'name' },
          { text: 'Score', value: 'score' },
          { text: this.groupLabel, value: 'group' },
          { text: 'Job Title', value: 'jobTitle' },
          { text: 'Absent Days', value: 'absentDays' },
          { text: 'Unapproved Absent', value: 'unApprovedAbsent' },
          { text: 'Late Hrs', value: 'lateHrs' },
          { text: 'Missed Punches', value: 'missedPunches' },
        ]
      },
      leastHeaders () {
        return [
          { text: 'Name', value: 'name' },
          { text: 'Score', value: 'score' },
          { text: this.groupLabel, value: 'group' },
          { text: 'Job Title', value: 'jobTitle' },
          { text: 'Absent Days', value: 'absentDays' },
          { text: 'Unapproved Absent', value: 'unApprovedAbsent' },
          { text: 'Late Hrs', value: 'lateHrs' },
          { text: 'Missed Punches', value: 'missedPunches' },
        ]
      },
      startDateFormatted () {
        if (!this.startDate) return ''
        return moment(this.startDate).format('DD/MM/YYYY')
      },
      endDateFormatted () {
        if (!this.endDate) return ''
        return moment(this.endDate).format('DD/MM/YYYY')
      },
    },
    apollo: {
      topN: {
        query: Q_GET_TIME_PERFORMANCE_REPORT,
        variables () {
          return {
            startDate: this.startDate,
            endDate: this.endDate,
            currentTeam: this.team,
            topNLimit: this.limit,
            leastNLimit: this.limit,
          }
        },
        update (data) {
          return data.timePerformanceReport.topN || []
        },
      },
      leastN: {
        query: Q_GET_TIME_PERFORMANCE_REPORT,
        variables () {
          return {
            startDate: this.startDate,
            endDate: this.endDate,
            currentTeam: this.team,
            topNLimit: this.limit,
            leastNLimit: this.limit,
          }
        },
        update (data) {
          return data.timePerformanceReport.leastN || []
        },
      },
    },
    watch: {
      limit () {
        this.$apollo.queries.topN.refetch()
        this.$apollo.queries.leastN.refetch()
      },
    },
  }
</script>

<style scoped>

</style>
